import { Form, InputGroup } from 'react-bootstrap';
import { useFormContext } from 'react-hook-form';
import { useSelector } from 'react-redux';

import { getCountry } from '@domains/user/store/slices/user-slice/user-slice';
import { CountryCode } from '@domains/country';
import { WrapperInputField } from '@shared/InputField/styles';
import {
  ACCESSORY_AREA_KIND_OTHER,
  createEmptyAccessoryArea,
  AccessoryAreaFormItem,
  AccessoryRoofedValue,
  ACCESSORY_AREA_KINDS_MX,
  getAccessoryRoofFieldState,
  resolveRoofedOnKindChange,
} from '@domains/documents-review/utils/accessoryAreas';
import {
  AccessoryAreaCard,
  AccessoryAreaCardHeader,
  AccessoryAreasHeader,
  AccessoryAreasSection,
  AddAreaButton,
  EmptyState,
  RemoveAreaButton,
} from './AccessoryAreasField.styles';

export const AccessoryAreasField = () => {
  const country = useSelector(getCountry) as CountryCode;
  const { watch, setValue } = useFormContext();

  if (country !== CountryCode.MX) return null;

  const areas: AccessoryAreaFormItem[] = watch('accessoryAreas') ?? [];
  const kindOptions = ACCESSORY_AREA_KINDS_MX;

  const updateAreas = (next: AccessoryAreaFormItem[]) => {
    setValue('accessoryAreas', next, {
      shouldDirty: true,
      shouldValidate: true,
    });
  };

  const updateArea = (
    index: number,
    patch: Partial<AccessoryAreaFormItem>
  ) => {
    updateAreas(
      areas.map((area, i) => (i === index ? { ...area, ...patch } : area))
    );
  };

  const handleAddArea = () => {
    updateAreas([...areas, createEmptyAccessoryArea()]);
  };

  const handleRemoveArea = (index: number) => {
    updateAreas(areas.filter((_, i) => i !== index));
  };

  if (!kindOptions.length) return null;

  return (
    <AccessoryAreasSection>
      <AccessoryAreasHeader>
        <p className="section-title">Áreas accesorias (opc)</p>
        <AddAreaButton type="button" onClick={handleAddArea}>
          Agregar área
        </AddAreaButton>
      </AccessoryAreasHeader>

      {!areas.length && (
        <EmptyState>Sin áreas accesorias registradas.</EmptyState>
      )}

      {areas.map((area, index) => {
        const roofState = getAccessoryRoofFieldState(area.kind, country);
        const roofedValue = area.roofed ?? roofState.roofed ?? 0;
        const fieldId = area.id;

        return (
          <AccessoryAreaCard key={area.id}>
            <AccessoryAreaCardHeader>
              <span className="area-index">Área {index + 1}</span>
              <RemoveAreaButton
                type="button"
                onClick={() => handleRemoveArea(index)}
              >
                Eliminar
              </RemoveAreaButton>
            </AccessoryAreaCardHeader>

            <WrapperInputField>
              <label className="form-label" htmlFor={`accessory-kind-${fieldId}`}>
                Tipo de área
              </label>
              <Form.Select
                id={`accessory-kind-${fieldId}`}
                className="form-select"
                value={area.kind}
                onChange={e => {
                  const kind = e.target.value;
                  updateArea(index, {
                    kind,
                    customKind:
                      kind === ACCESSORY_AREA_KIND_OTHER
                        ? area.customKind ?? ''
                        : undefined,
                    roofed: resolveRoofedOnKindChange(
                      kind,
                      country,
                      area.roofed
                    ),
                  });
                }}
              >
                <option value="">Seleccione una opción</option>
                {kindOptions.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </Form.Select>
            </WrapperInputField>

            {area.kind === ACCESSORY_AREA_KIND_OTHER && (
              <WrapperInputField>
                <label
                  className="form-label"
                  htmlFor={`accessory-custom-kind-${fieldId}`}
                >
                  Especifique el tipo de área
                </label>
                <Form.Control
                  id={`accessory-custom-kind-${fieldId}`}
                  type="text"
                  maxLength={50}
                  placeholder="Ej. Bodega"
                  value={area.customKind ?? ''}
                  onChange={e =>
                    updateArea(index, {
                      customKind: e.target.value.replace(/,/g, ' '),
                    })
                  }
                />
              </WrapperInputField>
            )}

            <div className="d-flex gap-2">
              <WrapperInputField>
                <label
                  className="form-label"
                  htmlFor={`accessory-meters-${fieldId}`}
                >
                  Metraje
                </label>
                <InputGroup>
                  <Form.Control
                    id={`accessory-meters-${fieldId}`}
                    type="text"
                    inputMode="numeric"
                    maxLength={3}
                    placeholder="0"
                    value={area.meters}
                    onChange={e =>
                      updateArea(index, {
                        meters: e.target.value.replace(/\D/g, ''),
                      })
                    }
                  />
                  <InputGroup.Text>m²</InputGroup.Text>
                </InputGroup>
              </WrapperInputField>

              {roofState.showRoofed && (
                <WrapperInputField>
                  <label
                    className="form-label"
                    htmlFor={`accessory-roofed-${fieldId}`}
                  >
                    Techada
                  </label>
                  <Form.Select
                    id={`accessory-roofed-${fieldId}`}
                    className="form-select"
                    value={roofedValue}
                    onChange={e =>
                      updateArea(index, {
                        roofed: Number(e.target.value) as AccessoryRoofedValue,
                      })
                    }
                  >
                    <option value={1} disabled={roofState.disableRoofed}>
                      Techada
                    </option>
                    <option value={0} disabled={roofState.disableNotRoofed}>
                      Sin techar
                    </option>
                  </Form.Select>
                </WrapperInputField>
              )}
            </div>
          </AccessoryAreaCard>
        );
      })}
    </AccessoryAreasSection>
  );
};
